import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { authApi, type MeStatsOut } from '@/api/auth';
import { ApiError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { useWalletStore } from '@/store/wallet';
import { walletApi, walletNumbers } from '@/api/wallet';
import { getMe, deleteAdesao } from '@/api/alianca';
import { referralApi, type ReferralMeOut } from '@/api/referral';
import { painelApi } from '@/api/painel';
import {
  isNativeApp,
  nativeGoogleSignIn,
  nativeGoogleAvailable,
  isUserCancellation
} from '@/lib/nativeGoogle';
import { nativeAppleSignIn } from '@/lib/nativeApple';
import { useAppConfig } from '@/lib/config';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Display } from '@/components/ui/Typography';
import { formatBRL, formatKwh } from '@/lib/format';
import { formatPhone, unmaskDigits } from '@/lib/masks';
import { friendlyMessage } from '@/lib/errors';
import { unregisterPush } from '@/lib/push';
import { InstallPrompt } from '@/components/profile/InstallPrompt';
import { useNotificationsRow } from '@/components/profile/useNotificationsRow';
import { AURORA_ACCENT, AuroraCard } from '@/components/referral/AuroraCard';
import {
  ArrowRightLine,
  BellLine,
  ChartLine,
  ChevronRightLine,
  GiftLine,
  LogoutLine,
  ShieldLine,
  WalletLine,
  WhatsGlyph
} from '@/components/referral/icons';

const DISPLAY = 'Chillax, system-ui, sans-serif';

type Mode = 'view' | 'edit';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'B';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export default function Profile() {
  const user = useAuthStore((s) => s.user);
  const wallet = useWalletStore((s) => s.wallet);
  const setWallet = useWalletStore((s) => s.setWallet);

  const [mode, setMode] = useState<Mode>('view');
  const [stats, setStats] = useState<MeStatsOut | null>(null);

  useEffect(() => {
    authApi
      .meStats()
      .then(setStats)
      .catch(() => {});
    if (!wallet) {
      walletApi
        .get()
        .then(setWallet)
        .catch(() => {});
    }
  }, [setWallet, wallet]);

  if (!user) {
    return <div className="h-32 animate-pulse rounded-3xl bg-white shadow-soft" />;
  }

  if (mode === 'edit') {
    return <EditPanel onClose={() => setMode('view')} />;
  }

  return (
    <ViewPanel
      stats={stats}
      walletBalance={Number(wallet?.balance_brl ?? 0)}
      onEdit={() => setMode('edit')}
    />
  );
}

interface ViewPanelProps {
  stats: MeStatsOut | null;
  walletBalance: number;
  onEdit: () => void;
}

function ViewPanel({ stats, walletBalance, onEdit }: ViewPanelProps) {
  const user = useAuthStore((s) => s.user)!;
  // Indique e Ganhe — card aurora só com o programa ligado (best-effort;
  // falha ou flag off → some).
  const [referral, setReferral] = useState<ReferralMeOut | null>(null);
  useEffect(() => {
    referralApi
      .me()
      .then(setReferral)
      .catch(() => setReferral(null));
  }, []);
  const referralOn = !!referral?.enabled && !!referral.code;
  // "Ativar notificações" — só no app nativo e só sem permissão dada.
  const notifications = useNotificationsRow();
  // Painel do parceiro (CPO/investidor) — a linha só aparece pra quem o
  // backend diz ter vínculo. Flag off (404), erro ou sem org → some.
  const [painelOn, setPainelOn] = useState(false);
  useEffect(() => {
    painelApi
      .me()
      .then((m) => setPainelOn(m.orgs.length > 0))
      .catch(() => setPainelOn(false));
  }, []);
  const kwhTotal = stats ? Number(stats.kwh_total) : 0;
  const co2 = stats ? Number(stats.co2_kg_avoided) : 0;
  const sessions = stats?.sessions_count ?? 0;
  const whatsappUrl =
    import.meta.env.VITE_SUPPORT_WHATSAPP_URL ?? 'https://wa.me/5527988231783';

  // NSS (design "Bow Perfil e Indique" · PerfilScreen, 390×844 sem scroll):
  // viewport fixa (100dvh − BottomNav), mesmo frame do Recibo. O miolo só
  // rola com fonte de acessibilidade grande. Comprime em tela baixa (≤740px).
  // O card "Permissões" que morava aqui saiu em 2026-10-08 (estranho pro
  // cliente e, cortado pelo frame, aparecia vazio): a localização é pedida
  // no contexto — aviso da Home e trava do ConfigureCharge. As notificações
  // voltaram em 2026-10-22 como UMA linha da lista de atalhos, que só aparece
  // enquanto a permissão não está dada (useNotificationsRow).
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height: 'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))'
      }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col px-5"
        style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto [@media(max-height:740px)]:gap-2.5">
          {/* Banner de instalação (PWA) — instância própria, dentro do frame */}
          <InstallPrompt />

          <h1
            className="m-0 flex-shrink-0 text-bow-royal [@media(max-height:740px)]:!text-[28px]"
            style={{
              fontFamily: DISPLAY,
              fontSize: 33,
              fontWeight: 600,
              letterSpacing: '-0.03em',
              lineHeight: 1
            }}
          >
            Perfil
          </h1>

          {/* Identidade + números */}
          <Card className="flex-shrink-0 px-5 pb-4 pt-[18px] [@media(max-height:740px)]:pb-3 [@media(max-height:740px)]:pt-3.5">
            <div className="flex items-center gap-[15px]">
              <div
                className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-full text-white [@media(max-height:740px)]:h-[52px] [@media(max-height:740px)]:w-[52px]"
                style={{
                  background: 'linear-gradient(135deg,#00C072 0%,#31B700 100%)',
                  boxShadow: '0 6px 16px rgba(0,192,114,0.30)',
                  fontFamily: DISPLAY,
                  fontWeight: 600,
                  fontSize: 21,
                  letterSpacing: '-0.01em'
                }}
              >
                {initialsOf(user.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="truncate text-bow-royal"
                  style={{
                    fontFamily: DISPLAY,
                    fontWeight: 500,
                    fontSize: 20,
                    letterSpacing: '-0.02em'
                  }}
                >
                  {user.name}
                </div>
                <div className="mt-0.5 truncate text-[13px] text-bow-royal/55">{user.email}</div>
              </div>
              <button
                type="button"
                onClick={onEdit}
                className="shrink-0 text-[13.5px] font-semibold text-bow-royal underline-offset-2 hover:underline"
              >
                Editar
              </button>
            </div>
            {stats && (
              <div className="mt-3.5 grid grid-cols-3 gap-2.5 border-t border-bow-royal/[0.07] pt-[13px] [@media(max-height:740px)]:mt-2.5 [@media(max-height:740px)]:pt-2.5">
                <ProfileStat value={String(sessions)} label="Recargas" />
                <ProfileStat value={formatKwh(kwhTotal).replace(' kWh', '')} label="kWh" />
                <ProfileStat value={`${co2.toFixed(0)} kg`} label="CO₂ evitado" />
              </div>
            )}
          </Card>

          {/* Indique e ganhe — card aurora (só com o programa ligado) */}
          {referralOn && referral && (
            <AuroraCard className="flex-shrink-0" padding="18px 20px 20px">
              <div className="flex items-start gap-4">
                <GiftLine size={66} />
                <div className="min-w-0 flex-1 pt-0.5">
                  <span
                    className="block text-[11.5px] font-semibold uppercase tracking-[0.07em]"
                    style={{ color: AURORA_ACCENT }}
                  >
                    Indique e ganhe
                  </span>
                  <div
                    className="mt-1.5"
                    style={{
                      fontFamily: DISPLAY,
                      fontWeight: 600,
                      fontSize: 26,
                      letterSpacing: '-0.025em',
                      lineHeight: 1.06
                    }}
                  >
                    {referral.referrer_percent}% de cashback
                  </div>
                  <div className="mt-[3px] text-[13.5px]" style={{ color: 'rgba(241,237,228,0.82)' }}>
                    quando seus amigos carregarem
                  </div>
                </div>
              </div>
              <Link
                to="/indique"
                className="mt-3.5 inline-flex h-12 items-center justify-center gap-3 rounded-full bg-bow-green px-[26px] text-[15.5px] font-semibold tracking-[-0.01em] text-white no-underline"
              >
                Convidar amigos <ArrowRightLine size={19} />
              </Link>
            </AuroraCard>
          )}

          {/* Atalhos */}
          <div className="flex-shrink-0 overflow-hidden rounded-3xl bg-white shadow-soft">
            <ProfileRow
              to="/wallet"
              icon={<WalletLine />}
              label="Carteira"
              value={formatBRL(walletBalance)}
            />
            {painelOn && (
              <ProfileRow to="/painel" icon={<ChartLine />} label="Painel do parceiro" />
            )}
            {notifications.copy && (
              <ProfileRow
                icon={<BellLine />}
                label={notifications.copy.label}
                sub={notifications.copy.sub ?? undefined}
                value={notifications.copy.value ?? undefined}
                onClick={notifications.copy.action ? notifications.activate : undefined}
                disabled={notifications.busy}
              />
            )}
            <ProfileRow
              href={whatsappUrl}
              icon={
                <span className="inline-flex text-bow-green">
                  <WhatsGlyph size={25} />
                </span>
              }
              label="Suporte pelo WhatsApp"
            />
            <ProfileRow
              href="https://bow.app.br/privacidade"
              icon={<ShieldLine />}
              label="Privacidade e termos"
              last
            />
          </div>

          <div className="min-h-[10px] flex-[1.2]" />
          <div className="flex flex-shrink-0 justify-center">
            <LogoutButton />
          </div>
          <div className="min-h-[8px] flex-1" />
        </div>
      </div>
    </div>
  );
}

function ProfileStat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div
        className="text-bow-royal"
        style={{
          fontFamily: DISPLAY,
          fontWeight: 500,
          fontSize: 21,
          letterSpacing: '-0.02em',
          lineHeight: 1.05
        }}
      >
        {value}
      </div>
      <div className="mt-[3px] text-[11.5px] font-medium text-bow-royal/55">{label}</div>
    </div>
  );
}

/**
 * Linha de atalho do Perfil — rota interna (`to`), externa (`href`, nova aba),
 * ação (`onClick`) ou só informativa (nenhum dos três: sem chevron).
 */
function ProfileRow({
  to,
  href,
  onClick,
  disabled = false,
  icon,
  label,
  sub,
  value,
  last = false
}: {
  to?: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  sub?: string;
  value?: string;
  last?: boolean;
}) {
  const interactive = !!(to || href || onClick);
  const className = `flex w-full items-center gap-[15px] px-5 py-[15px] text-left transition ${
    interactive ? 'hover:bg-bow-royal/5' : ''
  } ${last ? '' : 'border-b border-bow-royal/[0.07]'}`;
  const inner = (
    <>
      <span className="inline-flex shrink-0">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold tracking-[-0.01em] text-bow-royal">
          {label}
        </span>
        {sub && (
          <span className="mt-0.5 block text-[12.5px] leading-snug text-bow-royal/55">{sub}</span>
        )}
      </span>
      {value && <span className="shrink-0 text-[14.5px] font-medium text-bow-royal/55">{value}</span>}
      {interactive && (
        <span className="inline-flex shrink-0 text-bow-royal/35">
          <ChevronRightLine size={19} />
        </span>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${className} disabled:opacity-50`}
      >
        {inner}
      </button>
    );
  }
  if (!href) {
    return <div className={className}>{inner}</div>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {inner}
    </a>
  );
}

function LogoutButton() {
  const logout = useAuthStore((s) => s.logout);
  // Desregistra o token de push (best-effort, com sessão ainda válida) antes
  // de limpar o token — senão o aparelho continuaria recebendo push do user.
  const handleLogout = () => {
    void unregisterPush().finally(logout);
  };
  return (
    <button
      type="button"
      onClick={handleLogout}
      className="inline-flex items-center gap-2.5 text-[15px] font-medium text-bow-royal/55 transition hover:text-bow-royal"
    >
      <LogoutLine /> Sair da conta
    </button>
  );
}

interface EditPanelProps {
  onClose: () => void;
}

function EditPanel({ onClose }: EditPanelProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user)!;
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  // Populado no mount do Profile (pai) — usado no aviso de exclusão.
  const wallet = useWalletStore((s) => s.wallet);
  const walletBalance = Number(wallet?.balance_brl ?? 0);
  // Saldo negativo = pendência: o aviso da exclusão precisa dizer que a dívida
  // continua de pé (antes só existia o aviso de quem tinha saldo a favor).
  const walletDebt = walletNumbers(wallet).debt;

  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(formatPhone(user.phone ?? ''));
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileOk, setProfileOk] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState(false);

  // Conta só-Google/Apple não tem senha local (has_password === false).
  // Campo opcional: objetos antigos no store persistido não o têm → assume true.
  const hasPassword = user.has_password !== false;

  // Segundo fator da troca de email quando a conta NÃO tem senha: re-auth
  // social fresca (o backend confere o `sub`). Apple só no iOS nativo.
  const native = isNativeApp();
  const isIOS = native && Capacitor.getPlatform() === 'ios';
  const googleClientId = useAppConfig().google_client_id;
  const canApple = user.has_apple === true && isIOS;
  const canGoogle = user.has_google === true && !!googleClientId;
  const showNativeGoogle = native && nativeGoogleAvailable();

  const [emOpen, setEmOpen] = useState(false);
  const [emNew, setEmNew] = useState('');
  const [emPw, setEmPw] = useState('');
  const [emSaving, setEmSaving] = useState(false);
  const [emError, setEmError] = useState<string | null>(null);
  const [emSentTo, setEmSentTo] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [aliancaAtiva, setAliancaAtiva] = useState(false);
  const [confirmCancelAlianca, setConfirmCancelAlianca] = useState(false);
  const [cancelingAlianca, setCancelingAlianca] = useState(false);
  const [cancelAliancaError, setCancelAliancaError] = useState<string | null>(null);

  useEffect(() => {
    getMe()
      .then((m) => setAliancaAtiva(!!m.membership?.ativa))
      .catch(() => setAliancaAtiva(false));
    // pending_email/has_password podem estar stale no store persistido
    // (objetos de sessões antigas) — refetch garante o estado real.
    authApi
      .me()
      .then(setUser)
      .catch(() => {});
  }, [setUser]);

  async function onCancelAlianca() {
    setCancelingAlianca(true);
    setCancelAliancaError(null);
    try {
      await deleteAdesao();
      setAliancaAtiva(false);
      setConfirmCancelAlianca(false);
    } catch (err) {
      setCancelAliancaError(
        err instanceof ApiError
          ? friendlyMessage(err, 'Falha ao cancelar adesão')
          : 'Não foi possível conectar ao servidor'
      );
    } finally {
      setCancelingAlianca(false);
    }
  }

  const profileDirty =
    name.trim() !== user.name || unmaskDigits(phone) !== (user.phone ?? '');

  const pwLengthOk = pwNew.length >= 8;
  const pwHasUpper = /[A-Z]/.test(pwNew);
  const pwHasDigit = /\d/.test(pwNew);
  const pwMatch = pwNew === pwConfirm;
  const pwReady =
    pwCurrent.length > 0 && pwLengthOk && pwHasUpper && pwHasDigit && pwMatch;

  // Segundo fator da troca de email: senha (conta com senha) OU re-auth social
  // (conta sem senha). O botão social só habilita com o novo email válido.
  const emValid = /\S+@\S+\.\S+/.test(emNew.trim());
  const emReady = emValid && emPw.length > 0;

  async function onSaveProfile() {
    setProfileError(null);
    setProfileOk(false);
    setSaving(true);
    try {
      const updated = await authApi.updateProfile({
        name: name.trim(),
        phone: unmaskDigits(phone)
      });
      setUser(updated);
      setProfileOk(true);
      window.setTimeout(() => setProfileOk(false), 2500);
    } catch (err) {
      setProfileError(
        err instanceof ApiError
          ? friendlyMessage(err, 'Falha ao salvar')
          : 'Não foi possível conectar ao servidor'
      );
    } finally {
      setSaving(false);
    }
  }

  async function onSavePassword() {
    setPwError(null);
    setPwOk(false);
    setPwSaving(true);
    try {
      await authApi.changePassword({
        current_password: pwCurrent,
        new_password: pwNew
      });
      setPwOk(true);
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
      window.setTimeout(() => {
        setPwOk(false);
        setPwOpen(false);
      }, 2500);
    } catch (err) {
      setPwError(
        err instanceof ApiError
          ? friendlyMessage(err, 'Falha ao trocar senha')
          : 'Não foi possível conectar ao servidor'
      );
    } finally {
      setPwSaving(false);
    }
  }

  async function submitEmailChange(factor: {
    currentPassword?: string;
    googleIdToken?: string;
    appleIdToken?: string;
  }) {
    setEmError(null);
    setEmSaving(true);
    try {
      await authApi.requestEmailChange(emNew.trim(), factor);
      setEmSentTo(emNew.trim());
      setEmNew('');
      setEmPw('');
      // Reflete o pending_email fresco no store (nota de pendência).
      authApi
        .me()
        .then(setUser)
        .catch(() => {});
    } catch (err) {
      setEmError(
        err instanceof ApiError
          ? friendlyMessage(err, 'Falha ao pedir a troca de email')
          : 'Não foi possível conectar ao servidor'
      );
    } finally {
      setEmSaving(false);
    }
  }

  // Re-auth social como segundo fator (conta sem senha). Reautentica no
  // provedor pra provar posse ATUAL da conta — o backend confere o `sub`.
  async function onConfirmGoogleNative() {
    if (!googleClientId || emSaving || !emValid) return;
    let idToken: string;
    try {
      idToken = await nativeGoogleSignIn(googleClientId);
    } catch (err) {
      if (!isUserCancellation(err)) {
        setEmError('Não foi possível confirmar com o Google. Tente novamente.');
      }
      return;
    }
    await submitEmailChange({ googleIdToken: idToken });
  }

  function onConfirmGoogleWeb(resp: CredentialResponse) {
    if (!resp.credential) {
      setEmError('Google não retornou um token válido');
      return;
    }
    void submitEmailChange({ googleIdToken: resp.credential });
  }

  async function onConfirmApple() {
    if (emSaving || !emValid) return;
    let idToken: string;
    try {
      ({ idToken } = await nativeAppleSignIn());
    } catch {
      return; // cancelamento/erro — silencioso
    }
    await submitEmailChange({ appleIdToken: idToken });
  }

  async function onDelete() {
    setDeleting(true);
    try {
      await authApi.deleteAccount();
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      setProfileError(
        err instanceof ApiError
          ? friendlyMessage(err, 'Falha ao excluir conta')
          : 'Não foi possível conectar ao servidor'
      );
      setConfirmDelete(false);
      setDeleting(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
          aria-label="Voltar"
        >
          <ChevronLeft />
        </button>
        <Display size={22}>Editar perfil</Display>
      </div>

      {/* Cadastro */}
      <Card className="flex flex-col gap-3 p-5">
        <Input
          label="Nome"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/55">
            E-mail
          </span>
          <div className="flex h-12 items-center justify-between rounded-2xl border border-bow-royal/10 bg-bow-cream-2 px-4 text-bow-royal/65">
            <span className="truncate text-sm font-medium">{user.email}</span>
          </div>
          {user.pending_email && (
            <p className="px-1 text-xs text-bow-royal/55">
              Confirmação pendente para{' '}
              <strong className="text-bow-royal/80">{user.pending_email}</strong> —
              verifique a caixa de entrada (e o spam).
            </p>
          )}
        </div>

        <Input
          label="Celular"
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          name="bow-phone"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
        />

        {user.cpf && (
          <div className="flex flex-col gap-1.5">
            <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/55">
              CPF
            </span>
            <div className="flex h-12 items-center justify-between rounded-2xl border border-bow-royal/10 bg-bow-cream-2 px-4 text-bow-royal/65">
              <span className="text-sm font-medium">{user.cpf}</span>
              <span className="text-[11px]">não editável</span>
            </div>
          </div>
        )}

        {profileError && <p className="px-1 text-sm text-bow-red">{profileError}</p>}
        {profileOk && <p className="px-1 text-sm text-bow-green">Perfil atualizado.</p>}

        <Button onClick={onSaveProfile} disabled={!profileDirty || saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </Card>

      {/* Alterar email — accordion espelhando o de senha. O link de
          confirmação vai pro email NOVO; a troca só aplica após confirmar. */}
      <Card className="overflow-hidden p-0">
        <button
          type="button"
          onClick={() => {
            setEmOpen((v) => !v);
            // Reenvio prático: pré-preenche com o pending, se houver.
            if (!emOpen && !emNew && user.pending_email) {
              setEmNew(user.pending_email);
            }
          }}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <span className="font-semibold text-bow-royal">Alterar email</span>
          <span className="text-2xl text-bow-royal/40">{emOpen ? '−' : '+'}</span>
        </button>
        {emOpen && (
          <div className="flex flex-col gap-3 border-t border-bow-royal/5 px-5 py-4">
            {/* atual → novo, sem rótulos: o e-mail de agora fica à vista e a
                seta indica o destino — mata a confusão "qual é qual". */}
            <div className="truncate rounded-2xl bg-bow-cream-2 px-4 py-2.5 text-sm font-medium text-bow-royal/60">
              {user.email}
            </div>
            <div className="flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-bow-royal/45">
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 5v14M6 13l6 6 6-6" />
              </svg>
              trocar para
            </div>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="novo e-mail"
              value={emNew}
              onChange={(e) => setEmNew(e.target.value)}
            />

            {hasPassword ? (
              <>
                <Input
                  label="Sua senha"
                  type="password"
                  autoComplete="current-password"
                  value={emPw}
                  onChange={(e) => setEmPw(e.target.value)}
                />
                <Button
                  onClick={() => submitEmailChange({ currentPassword: emPw })}
                  disabled={!emReady || emSaving}
                >
                  {emSaving ? 'Enviando…' : 'Enviar confirmação'}
                </Button>
              </>
            ) : canApple ? (
              <button
                type="button"
                onClick={onConfirmApple}
                disabled={!emValid || emSaving}
                className="inline-flex h-[52px] items-center justify-center gap-2.5 rounded-full bg-[#111111] text-[15px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                {emSaving ? 'Confirmando…' : 'Confirmar com Apple'}
              </button>
            ) : canGoogle ? (
              native ? (
                showNativeGoogle ? (
                  <button
                    type="button"
                    onClick={onConfirmGoogleNative}
                    disabled={!emValid || emSaving}
                    className="inline-flex h-[52px] items-center justify-center gap-2.5 rounded-full border-[1.5px] border-bow-royal/20 bg-white text-[15px] font-semibold text-bow-royal transition hover:bg-bow-royal/5 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {emSaving ? 'Confirmando…' : 'Confirmar com Google'}
                  </button>
                ) : (
                  <p className="px-1 text-xs leading-relaxed text-bow-royal/60">
                    Pra confirmar, crie uma senha em{' '}
                    <Link to="/forgot-password" className="font-semibold text-bow-royal">
                      Esqueci minha senha
                    </Link>{' '}
                    (o link vai pro seu e-mail atual) e volte aqui.
                  </p>
                )
              ) : emValid ? (
                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={onConfirmGoogleWeb}
                    onError={() => setEmError('Falha ao confirmar com o Google')}
                    useOneTap={false}
                    width="320"
                    text="continue_with"
                    shape="pill"
                  />
                </div>
              ) : (
                <p className="px-1 text-xs text-bow-royal/50">
                  Digite o novo e-mail para confirmar com o Google.
                </p>
              )
            ) : (
              <p className="px-1 text-xs leading-relaxed text-bow-royal/60">
                Pra confirmar, crie uma senha em{' '}
                <Link to="/forgot-password" className="font-semibold text-bow-royal">
                  Esqueci minha senha
                </Link>{' '}
                (o link vai pro seu e-mail atual) e volte aqui.
              </p>
            )}

            <p className="px-1 text-xs leading-relaxed text-bow-royal/55">
              Enviaremos um link pro novo e-mail. A troca só vale depois que
              você confirmar por lá.
            </p>
            {emError && <p className="px-1 text-sm text-bow-red">{emError}</p>}
            {emSentTo && (
              <p className="px-1 text-sm text-bow-green">
                Link enviado para {emSentTo}. Confirme por lá pra concluir.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Alterar senha — conta só-Google/Apple não tem senha local: orienta a
          criar uma pelo "Esqueci minha senha" (define a 1ª senha; Google
          continua funcionando junto). */}
      {!hasPassword ? (
        <Card className="p-5">
          <p className="text-sm leading-relaxed text-bow-royal/65">
            Sua conta entra com Google/Apple e ainda não tem senha. Para criar
            uma, use{' '}
            <Link to="/forgot-password" className="font-semibold text-bow-royal">
              Esqueci minha senha
            </Link>{' '}
            com o seu email — o login social continua funcionando normalmente.
          </p>
        </Card>
      ) : (
      <Card className="overflow-hidden p-0">
        <button
          type="button"
          onClick={() => setPwOpen((v) => !v)}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <span className="font-semibold text-bow-royal">Alterar senha</span>
          <span className="text-2xl text-bow-royal/40">{pwOpen ? '−' : '+'}</span>
        </button>
        {pwOpen && (
          <div className="flex flex-col gap-3 border-t border-bow-royal/5 px-5 py-4">
            <Input
              label="Senha atual"
              type="password"
              autoComplete="current-password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
            />
            <Input
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
            />
            <Input
              label="Confirmar nova senha"
              type="password"
              autoComplete="new-password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
            />
            <ul className="flex flex-col gap-1 text-xs">
              <Hint ok={pwLengthOk}>Mínimo 8 caracteres</Hint>
              <Hint ok={pwHasUpper}>Ao menos 1 letra maiúscula</Hint>
              <Hint ok={pwHasDigit}>Ao menos 1 número</Hint>
              <Hint ok={pwMatch && pwNew.length > 0}>Confere com a confirmação</Hint>
            </ul>
            {pwError && <p className="px-1 text-sm text-bow-red">{pwError}</p>}
            {pwOk && <p className="px-1 text-sm text-bow-green">Senha alterada.</p>}
            <Button onClick={onSavePassword} disabled={!pwReady || pwSaving}>
              {pwSaving ? 'Trocando…' : 'Trocar senha'}
            </Button>
          </div>
        )}
      </Card>
      )}

      {/* Cancelar adesão à Aliança — discreto (só se ativa) */}
      {aliancaAtiva && (
        <div className="px-1 pt-1 text-center">
          {!confirmCancelAlianca ? (
            <button
              type="button"
              onClick={() => setConfirmCancelAlianca(true)}
              className="text-xs font-medium text-bow-royal/40 underline-offset-2 hover:underline"
            >
              Cancelar adesão à Aliança
            </button>
          ) : (
            <div className="flex flex-col gap-2 rounded-2xl border border-bow-royal/10 bg-white p-4 text-left">
              <span className="text-xs text-bow-royal/65">
                Ao cancelar, você perde seu histórico de kWh da Aliança. Se readerir,
                começa em Vermelho do zero.
              </span>
              {cancelAliancaError && (
                <span className="text-xs text-bow-red">{cancelAliancaError}</span>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setConfirmCancelAlianca(false);
                    setCancelAliancaError(null);
                  }}
                  disabled={cancelingAlianca}
                >
                  Voltar
                </Button>
                <button
                  type="button"
                  onClick={onCancelAlianca}
                  disabled={cancelingAlianca}
                  className="h-11 rounded-full bg-bow-red text-sm font-semibold text-white disabled:opacity-50"
                >
                  {cancelingAlianca ? 'Cancelando…' : 'Sim, cancelar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Excluir conta — discreto */}
      <div className="px-1 pb-2 pt-1 text-center">
        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="text-xs font-medium text-bow-royal/40 underline-offset-2 hover:underline"
          >
            Excluir minha conta
          </button>
        ) : (
          <div className="flex flex-col gap-2 rounded-2xl border border-bow-royal/10 bg-white p-4 text-left">
            <span className="text-xs text-bow-royal/65">
              Sua conta será desativada e você perde o acesso imediatamente. Wallet e
              histórico de transações ficam preservados pelo período legal de retenção
              (5 anos).
            </span>
            {walletDebt > 0 ? (
              <span className="rounded-xl bg-bow-red/10 px-3 py-2 text-xs font-semibold text-bow-red">
                Você tem {formatBRL(walletDebt)} pendentes. A exclusão não cancela
                valores devidos.
              </span>
            ) : (
              walletBalance > 0 && (
                <span className="rounded-xl bg-bow-red/10 px-3 py-2 text-xs font-semibold text-bow-red">
                  Você tem {formatBRL(walletBalance)} de saldo em carteira. Saldo e bônus
                  não são transferidos nem devolvidos na exclusão (Termos de Uso).
                </span>
              )
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="h-11 rounded-full bg-bow-red text-sm font-semibold text-white disabled:opacity-50"
              >
                {deleting ? 'Excluindo…' : 'Sim, excluir'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Hint({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li
      className={`flex items-center gap-1.5 ${ok ? 'text-bow-green' : 'text-bow-royal/55'}`}
    >
      <span>{ok ? '✓' : '○'}</span>
      <span>{children}</span>
    </li>
  );
}

function ChevronLeft() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}
